import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import { TERMS_SECTIONS, TERMS_VERSION, ACCEPTANCE_TEXT } from "@/lib/terms";
import { useAcceptTerms } from "@/hooks";
import { useResponsiveDialog } from "@/hooks/useResponsiveDialog";

interface TermsModalProps {
  open: boolean;
  onAccepted: () => void;
}

export function TermsModal({ open, onAccepted }: TermsModalProps) {
  const [accepted, setAccepted] = useState(false);
  const acceptTermsMutation = useAcceptTerms();
  const { fullScreen, TransitionComponent } = useResponsiveDialog();

  const handleAccept = async () => {
    try {
      await acceptTermsMutation.mutateAsync(TERMS_VERSION);
      onAccepted();
    } catch {
      // Error is handled by the mutation
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      TransitionComponent={TransitionComponent}
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          height: fullScreen ? "100%" : "95vh",
          maxHeight: fullScreen ? "100%" : "95vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 3,
        }}
      >
        <Typography variant="h5" component="div" fontWeight="bold">
          Terms & Conditions
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
          Please read and accept before continuing
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Version {TERMS_VERSION} | My Agent Report Ltd
          </Typography>

          {TERMS_SECTIONS.map((section, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ mb: 1, color: "text.primary" }}
              >
                {section.title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ lineHeight: 1.7 }}
              >
                {section.content}
              </Typography>
              {index < TERMS_SECTIONS.length - 1 && <Divider sx={{ mt: 3 }} />}
            </Box>
          ))}
        </Box>
      </DialogContent>

      <Box
        sx={{
          px: 3,
          py: 2,
          bgcolor: "grey.50",
          borderTop: 1,
          borderColor: "divider",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {ACCEPTANCE_TEXT}
            </Typography>
          }
        />
      </Box>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          disabled={!accepted || acceptTermsMutation.isPending}
          onClick={handleAccept}
          sx={{ minWidth: 150 }}
        >
          {acceptTermsMutation.isPending ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "I Accept"
          )}
        </Button>
      </DialogActions>

      {acceptTermsMutation.isError && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography color="error" variant="body2">
            Failed to save acceptance. Please try again.
          </Typography>
        </Box>
      )}
    </Dialog>
  );
}
